import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createCarousalData,
  editCarousalAPI,
  fetchAllCarouselData,
  updateCarousalAPI,
  getScrollInfoAPI,
  updateScrollInfoAPI,
} from "../apis/carousel.api";
import { errorToastify, successToastify } from "../utils/toastify.util";

const initialState = {
  isLoad: true,
  listData: [],
  formData: null,
  isUpdate: false,
  isAdd: false,
  isLoadStatus: false,

  // Edit state
  isEdit: false,
  editId: null,

  // Type options
  type: [
    { label: "Hunt", value: 0 },
    { label: "Quest", value: 1 },
    { label: "Both", value: 2 },
  ],
  edit_type: [
    { label: "Hunt", value: 0 },
    { label: "Quest", value: 1 },
  ],

  // Form fields
  selectedType: "",
  carouselImage: "",
  carouselLink_Url: "",
  sequence: 0,

  // Scroll Info
  scrollInfo: {
    id: 1,
    isHunt: 0,
    isQuest: 0,
  },
  isScrollLoad: false,
  isScrollSaving: false,
  timing:0
};

/**
 * List: GET /api/v1/carousel/getAllCarouselData
 */
export const fetchAllCarousel = createAsyncThunk(
  "carousel/fetchAllCarousel",
  async () => {
    try {
      const response = await fetchAllCarouselData();
      return response;
    } catch (err) {
      const { data } = err.response || {};
      return data || { success: false, message: "Failed to fetch carousel list" };
    }
  }
);

/**
 * Create: POST /api/v1/carousel/addCarousal
 */
export const createCarousel = createAsyncThunk(
  "carousel/createCarousel",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const data = await createCarousalData(payload);
      if (data?.success) {
        const msg =
          data?.message || data?.mesasge || "Carousal created successfully";
        successToastify(msg);
        dispatch(setSelectedType(""));
        dispatch(setCarouselImage(""));
        dispatch(setSequence(""))
        dispatch(fetchAllCarousel());
      } else {
        errorToastify(data?.message || data?.mesasge || "Create failed");
      }
      return data;
    } catch (err) {
      const { data } = err.response || {};
      errorToastify(data?.message || data?.mesasge || "Create failed");
      return rejectWithValue(data || { success: false, message: "Create failed" });
    }
  }
);

/**
 * Update Status: POST /api/v1/carousel/update_status
 */
export const modifyCarousalImageStatus = createAsyncThunk(
  "carousal/modifyCarousalImageStatus",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const data = await updateCarousalAPI(payload);
      if (data?.success) {
        const msg =
          data?.message || data?.mesasge || "Carousal Image status updated";
        successToastify(msg);
        dispatch(fetchAllCarousel());
      } else {
        errorToastify(data?.message || data?.mesasge || "Update status failed");
      }
      return data;
    } catch (err) {
      const { data } = err.response || {};
      errorToastify(data?.message || data?.mesasge || "Update status failed");
      return rejectWithValue(data || { success: false, message: "Update status failed" });
    }
  }
);

/**
 * Edit Carousel: POST /api/v1/carousel/update_carousel
 */
export const updateCarousel = createAsyncThunk(
  "carousel/updateCarousel",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const data = await editCarousalAPI(payload);
      if (data?.success) {
        const msg =
          data?.message || data?.mesasge || "Carousel updated successfully";
        successToastify(msg);
        dispatch(setSelectedType(""));
        dispatch(setCarouselImage(""));
        dispatch(setSequence(""))
        dispatch(setIsEditMode({ isEdit: false })); // Reset edit mode
        dispatch(fetchAllCarousel());
      } else {
        errorToastify(data?.message || data?.mesasge || "Update failed");
      }
      return data;
    } catch (err) {
      const { data } = err.response || {};
      errorToastify(data?.message || data?.mesasge || "Update failed");
      return rejectWithValue(data || { success: false, message: "Update failed" });
    }
  }
);

/**
 * Scroll Info: GET /api/v1/carousel/getScrollInfo
 * API returns an array: result: [{ id, isHunt, isQuest, ... }]
 */
export const fetchScrollInfo = createAsyncThunk(
  "carousel/fetchScrollInfo",
  async () => {
    try {
      const data = await getScrollInfoAPI();
      return data;
    } catch (err) {
      const { data } = err.response || {};
      return data || { success: false, message: "Failed to fetch scroll info" };
    }
  }
);

/**
 * Scroll Info Update: POST /api/v1/carousel/updateScrollInfo
 * payload: { id: 1, isHunt: 0|1, isQuest: 0|1 }
 * After success, re-fetch latest via fetchScrollInfo()
 */
export const saveScrollInfo = createAsyncThunk(
  "carousel/saveScrollInfo",
  async (payload, { getState, dispatch, rejectWithValue }) => {
    try {
      const { scrollInfo } = getState().carousel;

      const changed =
        Number(scrollInfo.isHunt) !== Number(payload.isHunt) ||
        Number(scrollInfo.isQuest) !== Number(payload.isQuest);

      if (!changed) {
        return { success: true, noChange: true };
      }

      const res = await updateScrollInfoAPI(payload);

      if (res?.success) {
        // Immediately load the latest values from server
        dispatch(fetchScrollInfo());
      } else {
        errorToastify(res?.message || "Failed to update scroll settings");
      }

      return res;
    } catch (err) {
      const { data } = err.response || {};
      return rejectWithValue(
        data || { success: false, message: "Failed to update scroll info" }
      );
    }
  }
);

const carouselSlice = createSlice({
  name: "carousel",
  initialState,
  reducers: {
    setFormData: (state, { payload }) => {
      state.formData = { ...state.formData, ...payload };
    },
    setSelectedType: (state, { payload }) => {
      state.selectedType = payload;
    },
    setCarouselImage: (state, { payload }) => {
      state.carouselImage = payload;
    },
    setCarouselLink: (state, { payload }) => {
      state.carouselLink_Url = payload;
    },
    setSequence: (state, { payload }) => {
      state.sequence = payload;
    },
    setTiming: (state, { payload }) => {
      state.timing = payload;
    },
    setIsEditMode: (state, { payload }) => {
      state.isEdit = !!payload.isEdit;
      state.editId = payload.editId ?? state.editId;
      if (payload.selectedType !== undefined) state.selectedType = payload.selectedType;
      if (payload.carouselImage !== undefined) state.carouselImage = payload.carouselImage;
      if (payload.carouselLink_Url !== undefined) state.carouselLink_Url = payload.carouselLink_Url;
      if (payload.sequence !== undefined) state.sequence = payload.sequence;
      if (payload.timing !== undefined) state.timing = payload.timing;
    },
    resetCarouselForm: (state) => {
      state.selectedType = "";
      state.carouselImage = "";
      state.carouselLink_Url = "";
      state.sequence=""
      state.isEdit = false;
      state.editId = null;
      state.timing = 0
    },
  },
  extraReducers: (builder) => {
    /**
     * List
     */
    builder.addCase(fetchAllCarousel.pending, (state) => {
      state.isLoad = true;
    });
    builder.addCase(fetchAllCarousel.fulfilled, (state, action) => {
      state.isLoad = false;
      if (action.payload?.success) {
        state.isUpdate = false;
        state.listData = action.payload.result || [];
      } else {
        errorToastify(action.payload?.message || "Failed to fetch carousel list");
      }
    });
    builder.addCase(fetchAllCarousel.rejected, (state) => {
      state.isLoad = false;
      errorToastify("Request is rejected..");
    });

    /**
     * Create
     */
    builder.addCase(createCarousel.pending, (state) => {
      state.isAdd = true;
    });
    builder.addCase(createCarousel.fulfilled, (state) => {
      state.isAdd = false;
    });
    builder.addCase(createCarousel.rejected, (state) => {
      state.isAdd = false;
      errorToastify("Request is rejected..");
    });

    /**
     * Modify status
     */
    builder.addCase(modifyCarousalImageStatus.pending, (state) => {
      state.isLoadStatus = true;
    });
    builder.addCase(modifyCarousalImageStatus.fulfilled, (state) => {
      state.isLoadStatus = false;
    });
    builder.addCase(modifyCarousalImageStatus.rejected, (state) => {
      state.isLoadStatus = false;
      errorToastify("Request is rejected..");
    });

    /**
     * Update carousel
     */
    builder.addCase(updateCarousel.pending, (state) => {
      state.isAdd = true;
    });
    builder.addCase(updateCarousel.fulfilled, (state) => {
      state.isAdd = false;
      state.isEdit = false;
      state.editId = null;
    });
    builder.addCase(updateCarousel.rejected, (state) => {
      state.isAdd = false;
      errorToastify("Request is rejected..");
    });

    /**
     * Scroll Info (GET)
     * Parse array result; map first row to state.scrollInfo
     */
    builder.addCase(fetchScrollInfo.pending, (state) => {
      state.isScrollLoad = true;
    });
    builder.addCase(fetchScrollInfo.fulfilled, (state, action) => {
      state.isScrollLoad = false;
      const payload = action.payload;
      if (
        payload?.success &&
        Array.isArray(payload?.result) &&
        payload.result.length > 0
      ) {
        const row = payload.result[0];
        state.scrollInfo = {
          id: row?.id ?? 1,
          isHunt: Number(row?.isHunt) || 0,
          isQuest: Number(row?.isQuest) || 0,
        };
      } else {
        errorToastify(payload?.message || "Failed to fetch scroll info");
      }
    });
    builder.addCase(fetchScrollInfo.rejected, (state) => {
      state.isScrollLoad = false;
      errorToastify("Failed to fetch scroll info");
    });

    /**
     * Scroll Info (POST)
     */
    builder.addCase(saveScrollInfo.pending, (state) => {
      state.isScrollSaving = true;
    });
    builder.addCase(saveScrollInfo.fulfilled, (state, action) => {
      state.isScrollSaving = false;
      if (action.payload?.noChange) return;
      if (action.payload?.success) {
        successToastify("Autoplay settings updated");
        // No direct mutation here; fetchScrollInfo() has already run to refresh state.
      } else {
        errorToastify(action.payload?.message || "Failed to update scroll settings");
      }
    });
    builder.addCase(saveScrollInfo.rejected, (state, action) => {
      state.isScrollSaving = false;
      errorToastify(action.payload?.message || "Failed to update scroll settings");
    });
  },
});

export default carouselSlice.reducer;
export const {
  setFormData,
  setSelectedType,
  setCarouselImage,
  setCarouselLink,
  setSequence,
  setIsEditMode,
  resetCarouselForm,
  setTiming
} = carouselSlice.actions;
